import React from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Col, Progress, Row } from "reactstrap";
import styles from "./styles.module.css";
import { useSelector } from "react-redux";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { Icon } from "@iconify/react";
import { getCreditText } from "../../../../helpers/common";
import moment from "moment";

const CreditPanel = () => {
  const navigate = useNavigate();
  const employerReducer = useSelector((state) => state.employer);
  const isPlanLoading = employerReducer?.isPlanLoading;
  const planDetails = employerReducer?.planDetails;
  const plan = planDetails?.plan;

  const totalCredits = planDetails?.credits || 0;
  const totalDebits = planDetails?.debits || 0;
  const totalBalance = planDetails?.balance || 0;

  const creditUsedPercentage = (totalDebits / totalCredits) * 100;
  const isCreditFinish = totalBalance <= 0;

  const isPlanExpired = planDetails?.is_expired || false;

  const renderTooltip = (props, message) => (
    <Tooltip id="button-tooltip" {...props}>
      {message}
    </Tooltip>
  );

  const getExpiryDateText = () => {
    const expiryDate = moment(planDetails?.credits_expiry_date).format(
      "MMMM DD, YYYY"
    );

    if (isPlanExpired) {
      return (
        <p className="mb-0 text-danger" style={{ fontSize: "12px" }}>
          Your credits are expired on <span className="fw-bold">{expiryDate}</span>,
          If you want to continue using the credits, please renew your plan.
        </p>
      );
    }
    return (
      <p className="mb-0 text-muted" style={{ fontSize: "12px" }}>
        Your credits are available till <span className="fw-bold">{expiryDate}</span>
      </p>
    );
  };

  if (isPlanLoading) {
    return null;
  }

  // Show no plan card if no plan exists
  if (!planDetails?.plan) {
    return (
      <div className={styles.noPlanContainer}>
        <Row className="align-items-center">
          <Col lg={8}>
            <div className={styles.noPlanLeft}>
              <Icon
                icon="material-symbols:credit-card-outline"
                fontSize={45}
                className={styles.noPlanIcon}
              />
              <h3 className={styles.noPlanTitle}>No Active Plan</h3>
              <p className={styles.noPlanText}>
                You don't have an active plan on your account. Activate a plan today to enjoy unlimited job postings, unlimited candidate responses, and access to our full candidate database.
              </p>
            </div>
          </Col>
          <Col lg={4}>
            <div className={`${styles.noPlanRight} text-center`}>
              <button
                type="button"
                className={`btn btn-light btn-lg ${styles.noPlanButton}`}
                onClick={() => navigate("/pricing")}
              >
                <Icon icon="material-symbols:shopping-cart" className="me-2" />
                View Plans & Pricing
              </button>
            </div>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className={styles.trackerContainer}>
      <Row>
        <Col lg={4}>
          <div className={styles.leftBox}>
            <div className="d-flex align-items-center mb-2">
              <p className="mb-0 fw-600 me-2">Current plan</p>
              {isPlanExpired ? (
                <Badge color="danger" className="ms-2">
                  Expired
                </Badge>
              ) : (
                <Badge color="success" className="ms-2">
                  Active
                </Badge>
              )}
            </div>

            <div className="d-flex align-items-start pe-4">
              <h4 className="mb-0 pe-2">{plan?.name || "Unknown"}</h4>

              <div style={{ width: 20, marginTop: 3 }}>
                {plan?.description && (
                  <OverlayTrigger
                    placement="right"
                    delay={{ show: 250, hide: 400 }}
                    overlay={(e) => renderTooltip(e, plan?.description)}
                  >
                    <Icon
                      icon="material-symbols:info"
                      fontSize={20}
                      style={{ cursor: "pointer" }}
                    />
                  </OverlayTrigger>
                )}
              </div>
            </div>

            <div className="d-flex align-items-end">
              <h1 className="mb-0">{plan?.total_credits}</h1>
              <h6 className="mb-3 ms-2">credits</h6>
            </div>
          </div>
        </Col>
        <Col lg={8}>
          <div className="d-flex justify-content-between">
            <div>
              <h6 className="mb-0">{totalDebits}</h6>
              <p className="mb-2">{getCreditText(totalDebits)} used</p>
            </div>

            <div>
              <h6 className="mb-0">{totalBalance}</h6>
              <p className="mb-2">
                {getCreditText(totalBalance)}{" "}
                {isPlanExpired ? "expired" : "available"}
              </p>
            </div>
          </div>

          <Progress
            value={creditUsedPercentage}
            color={
              isCreditFinish ? "danger" : isPlanExpired ? "warning" : "primary"
            }
            className={styles.progress}
          />

          <div className="d-flex justify-content-between align-items-center mt-4">
            {totalCredits && (
              <div>
                <p className="mb-0">Total credits purchased</p>
                <h6 className="mb-0">{totalCredits}</h6>
              </div>
            )}

            <div className="d-flex gap-2">
              {isPlanExpired && (
                <button
                  type="button"
                  className={`btn btn-danger ${styles.button}`}
                  onClick={() => navigate("/pricing")}
                >
                  Renew Plan
                </button>
              )}
              <button
                type="button"
                className={`btn btn-dark ${styles.button}`}
                onClick={() => navigate("/employer/orders")}
              >
                View details
              </button>
            </div>
          </div>

          <div className="mt-4 d-flex justify-content-end">
            {getExpiryDateText()}
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default CreditPanel;
